import Phaser from "phaser";
import { GAME_WIDTH } from "../config";

interface HudState {
  primaryLabel: string; // "STRENGTH" (engulf) or "POWER" (shoot)
  primaryValue: number;
  points: number;
  remaining: number;
  health: number; // shoot only; 0 maxHealth hides the bar
  maxHealth: number;
  shieldSecs: number; // engulf
  rapidSecs: number; // shoot
  comboMult: number; // engulf; 0 hides
}

/** Adapts to both modes: shows Strength/Power, optional health bar, and the
 *  relevant timer (shield or rapid-fire), plus score, enemies-left, and buttons. */
export default class Hud {
  private primaryLabel: Phaser.GameObjects.Text;
  private primaryValue: Phaser.GameObjects.Text;
  private pointsText: Phaser.GameObjects.Text;
  private remainingText: Phaser.GameObjects.Text;
  private statusText: Phaser.GameObjects.Text;
  private comboText: Phaser.GameObjects.Text;
  private healthBar: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, onPause: () => void, onHome: () => void) {
    this.makeIconButton(scene, 44, 44, "❚❚", onPause);
    this.makeIconButton(scene, 108, 44, "⌂", onHome);

    this.statusText = scene.add
      .text(24, 84, "", { fontFamily: "Arial, sans-serif", fontSize: "22px", color: "#9fe8ff" })
      .setScrollFactor(0)
      .setDepth(90);

    this.primaryLabel = scene.add
      .text(GAME_WIDTH / 2, 18, "STRENGTH", { fontFamily: "Arial, sans-serif", fontSize: "18px", color: "#9fd8ff" })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(90);

    this.primaryValue = scene.add
      .text(GAME_WIDTH / 2, 36, "10", {
        fontFamily: "Arial, sans-serif",
        fontSize: "58px",
        color: "#eaf6ff",
        fontStyle: "bold",
        stroke: "#0a1020",
        strokeThickness: 6,
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(90);

    this.pointsText = scene.add
      .text(GAME_WIDTH - 22, 28, "★ 0", { fontFamily: "Arial, sans-serif", fontSize: "28px", color: "#bdf0d0", fontStyle: "bold" })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(90);

    this.remainingText = scene.add
      .text(24, 112, "", { fontFamily: "Arial, sans-serif", fontSize: "20px", color: "#9fd8ff" })
      .setScrollFactor(0)
      .setDepth(90);

    this.comboText = scene.add
      .text(GAME_WIDTH / 2, 134, "", { fontFamily: "Arial, sans-serif", fontSize: "26px", color: "#ffd86b", fontStyle: "bold" })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(90);

    this.healthBar = scene.add.graphics().setScrollFactor(0).setDepth(90);
  }

  private makeIconButton(scene: Phaser.Scene, x: number, y: number, icon: string, onTap: () => void): void {
    const bg = scene.add
      .rectangle(x, y, 56, 56, 0x12233f, 0.85)
      .setStrokeStyle(2, 0x9fd8ff, 0.6)
      .setScrollFactor(0)
      .setDepth(90)
      .setInteractive({ useHandCursor: true });
    scene.add
      .text(x, y, icon, { fontFamily: "Arial, sans-serif", fontSize: "26px", color: "#eaf6ff" })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(91);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, onTap);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => bg.setFillStyle(0x1f3d5c));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => bg.setFillStyle(0x12233f));
  }

  update(s: HudState): void {
    this.primaryLabel.setText(s.primaryLabel);
    this.primaryValue.setText(`${s.primaryValue}`);
    this.pointsText.setText(`★ ${s.points}`);
    this.remainingText.setText(`Left: ${s.remaining}`);
    this.comboText.setText(s.comboMult > 0 ? `Combo ×${s.comboMult}` : "");

    if (s.shieldSecs > 0) this.statusText.setText(`🛡 ${Math.ceil(s.shieldSecs)}s`);
    else if (s.rapidSecs > 0) this.statusText.setText(`⚡ ${Math.ceil(s.rapidSecs)}s`);
    else this.statusText.setText("");

    this.healthBar.clear();
    if (s.maxHealth > 0) this.drawHealth(s.health, s.maxHealth);
  }

  private drawHealth(health: number, maxHealth: number): void {
    const w = 240;
    const h = 16;
    const x = GAME_WIDTH / 2 - w / 2;
    const y = 104;
    const pct = Phaser.Math.Clamp(health / maxHealth, 0, 1);
    const g = this.healthBar;
    g.fillStyle(0x000000, 0.35);
    g.fillRoundedRect(x, y, w, h, 8);
    const color = pct > 0.5 ? 0x6bf0a4 : pct > 0.25 ? 0xffd86b : 0xff5d73;
    g.fillStyle(color, 0.95);
    g.fillRoundedRect(x, y, Math.max(0, w * pct), h, 8);
    g.lineStyle(2, 0xffffff, 0.35);
    g.strokeRoundedRect(x, y, w, h, 8);
  }
}
